/**
 * Sidebar contextual del módulo Llamadas — patrón Chatwoot/Linear.
 *
 * En cualquier ruta de Llamadas (`/llamadas/*` o las páginas de voz
 * dentro de CRM como `/llamadas/dialer`, `/llamadas/calls`, etc.) reemplaza
 * al Sidebar global. Usa Polaris `<Navigation>` + `<Navigation.Section>`.
 *
 * Replica el patrón de `WaInboxNavigation` (módulo-as-app con su propio mundo).
 */

import { useLocation } from 'react-router-dom';
import { Navigation } from '@/lib/polaris';
import {
  ArrowLeftIcon,
  PhoneIcon,
  CalendarTimeIcon,
  MicrophoneIcon,
  StarFilledIcon,
  SendIcon,
  ChartVerticalFilledIcon,
  PersonIcon,
  SettingsIcon,
  CartAbandonedIcon,
} from '@/lib/polaris-icons';
import { ROUTES, storePath } from '@/config/routes.config';
import { useAuth } from '@smarty/shared';

/**
 * Sub-items de "Ajustes" — config técnica del centro de llamadas.
 * Renombrado de "Administración" a "Ajustes" (más corto, estándar Apple/iOS).
 * M.1 (auditoría 2026-05-23): la URL raíz /llamadas ahora es el
 * HomePage del módulo (dashboard). Settings vive bajo /llamadas/settings.
 */
const ADMIN_SUBNAV: Array<{ route: string; label: string }> = [
  { route: ROUTES.APPS_CALL_CENTER_SETTINGS, label: 'Telefonía' },
  { route: ROUTES.APPS_CALL_CENTER_EXTENSIONS, label: 'Anexos' },
];

export function CallCenterNavigation() {
  const location = useLocation();
  const { storeSlug } = useAuth();

  const toUrl = (route: string) => (storeSlug ? storePath(storeSlug, route) : route);

  // ─── OPERACIÓN — day-to-day del agente ───
  const operationItems = [
    { url: toUrl(ROUTES.APPS_CRM_DIALER), label: 'Dialer', icon: PhoneIcon },
    { url: toUrl(ROUTES.APPS_CRM_CALLS), label: 'Llamadas', icon: CalendarTimeIcon },
  ];

  // ─── AGENTES IA — flows IA del Voice Commerce ───
  // C.1 (auditoría 2026-05-23): item duplicado "Voz del agente" removido.
  // La config de voz/TTS sigue accesible standalone (back-compat bookmarks)
  // pero ahora la entrada única es "Configuración del agente", que en su
  // página linkea a la config avanzada de voz.
  const agentsItems = [
    { url: toUrl(ROUTES.APPS_CALL_CENTER_CART_RECOVERY), label: 'Carritos', icon: CartAbandonedIcon },
    { url: toUrl(ROUTES.APPS_CALL_CENTER_COD_CONFIRMATION), label: 'Confirmación COD', icon: PhoneIcon },
    { url: toUrl(ROUTES.APPS_CALL_CENTER_COLLECTION), label: 'Cobranzas', icon: PhoneIcon },
    { url: toUrl(ROUTES.APPS_CALL_CENTER_POST_PURCHASE), label: 'NPS', icon: StarFilledIcon },
    { url: toUrl(ROUTES.APPS_CRM_VOICE_AI), label: 'Configuración del agente', icon: MicrophoneIcon },
  ];

  // ─── CAMPAÑAS ───
  // C.3 (auditoría 2026-05-23): item oculto temporalmente — CallCenterCampaignDetailPage
  // tiene placeholders ("próximamente") en el builder. URL accesible standalone
  // por back-compat; reactivar cuando el builder esté completo (Sprint 3) o cuando
  // Agent Platform absorba campañas como skill terminal.
  const campaignItems: Array<{ url: string; label: string; icon: typeof SendIcon }> = [];

  // ─── ANÁLISIS — todo lo que es reporte / post-mortem / supervisión ───
  // Consolidado: incluye los reportes que antes vivían dispersos en
  // "Voice Commerce & IA" (Métricas, Análisis de llamadas, Coaching).
  const analyticsItems = [
    { url: toUrl(ROUTES.APPS_CALL_CENTER_STATS), label: 'Métricas', icon: ChartVerticalFilledIcon },
    { url: toUrl(ROUTES.APPS_CALL_CENTER_VOICE_METRICS), label: 'Voice Commerce', icon: ChartVerticalFilledIcon },
    { url: toUrl(ROUTES.APPS_CALL_CENTER_CALL_ANALYSIS_LIST), label: 'Análisis llamadas', icon: ChartVerticalFilledIcon },
    { url: toUrl(ROUTES.APPS_CRM_CALL_COACHING), label: 'Coaching', icon: StarFilledIcon },
    { url: toUrl(ROUTES.APPS_CALL_CENTER_SUPERVISOR), label: 'Supervisor', icon: PersonIcon },
  ];

  // ─── AJUSTES (antes "Administración") ───
  const adminItems = [
    {
      // El padre apunta al primer sub-item (Telefonía) para que el click tenga
      // destino útil, igual que WaInboxNavigation hace con su sección Ajustes.
      url: toUrl(ROUTES.APPS_CALL_CENTER_SETTINGS),
      label: 'Ajustes',
      icon: SettingsIcon,
      subNavigationItems: ADMIN_SUBNAV.map(({ route, label }) => ({
        url: toUrl(route),
        label,
      })),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Navigation location={location.pathname}>
          <Navigation.Section
            items={[{ label: 'Volver', icon: ArrowLeftIcon, url: toUrl(ROUTES.HOME) }]}
          />
          <Navigation.Section title="Operación"  items={operationItems} separator />
          <Navigation.Section title="Agentes IA" items={agentsItems}    separator />
          {campaignItems.length > 0 && (
            <Navigation.Section title="Campañas" items={campaignItems} separator />
          )}
          <Navigation.Section title="Análisis"   items={analyticsItems} separator />
          <Navigation.Section title="Ajustes"    items={adminItems}     separator />
        </Navigation>
      </div>
    </div>
  );
}
